import * as React from "react";
import { homeLink, linksOf, request } from "../../../services/cpln";
import { RunCronReducerActionType, useRunCronReducer } from "./reducer_runCron";
import { AutoComplete, Button, Checkbox, Divider, Input, InputNumber, Modal, Select, Tabs, notification } from "antd";
import { Workload } from "../../../schema/types/workload/entity";
import { MinusCircleOutlined } from "@ant-design/icons";
import { fetchGvcLocations } from "../../../mst/kinds/gvc.helpers";
import { parseLink } from "../../../utils/linkParser/linkParser";
import { Image } from "../../../schema/types/image/entity";
import { ImageIcon } from "../../../components/Icons/svgComponents/ImageIcon";
import { RadioCardGroup } from "../../../components/antd/RadioCardGroup";

const cpuUnitOptions = [
  { label: "Millicores", value: "m" },
  { label: "Cores", value: "" },
];

const memoryUnitOptions = [
  { label: "Mi (Mebibytes)", value: "Mi" },
  { label: "Gi (Gibibytes)", value: "Gi" },
];

interface FormItemProps {
  label: string;
  children: React.ReactNode;
  className?: string;
}
const FormItem: React.FC<FormItemProps> = ({ label, children, className = "" }) => (
  <div className={`flex flex-col gap-1 ${className}`}>
    <label className="text-sm color-label">{label}</label>
    {children}
  </div>
);

interface Props {
  workload: Workload;
  initialLocation?: string;
  onClose: any;
  onDone: any;
  visible: boolean;
}

export const CommandModal_StartCronJob: React.FC<Props> = ({ workload, initialLocation, onClose, onDone, visible }) => {
  const [isLoading, setIsLoading] = React.useState(false);

  const [gvcLocations, setGVCLocations] = React.useState<string[]>([]);
  const [locationFetchFailed, setLocationFetchFailed] = React.useState(false);
  const [location, setLocation] = React.useState(initialLocation);
  // TODO Filter by the active workload locations
  const availableLocations = [...gvcLocations];
  if (!!initialLocation && !availableLocations.includes(initialLocation)) {
    availableLocations.push(initialLocation);
  }

  const [activeDeadlineSeconds, setActiveDeadlineSeconds] = React.useState<number | null>(null);

  const [images, setImages] = React.useState<Image[]>([]);
  const [isLoadingImages, setIsLoadingImages] = React.useState(false);

  const [state, dispatch] = useRunCronReducer(workload.spec.containers.map((c) => c.name));

  React.useEffect(() => {
    loadGvcLocations();
    loadImages();
  }, []);

  async function loadGvcLocations() {
    const selfLink = linksOf(workload).self;
    if (!selfLink) return;
    const gvcName = parseLink(selfLink, { useInputCtx: true }).gvc;
    if (!gvcName) return;
    try {
      const result = await fetchGvcLocations(gvcName);
      setGVCLocations(result.enabled);
      if (!initialLocation && result.enabled.length > 0) {
        setLocation(result.enabled[0]);
      }
    } catch (e) {
      setLocationFetchFailed(true);
    }
  }

  async function loadImages() {
    try {
      setIsLoadingImages(true);
      const url = homeLink("image");
      const { data } = await request<{ items: Image[] }>({ url });
      setImages(data.items || []);
    } catch (e) {
      // Ignore errors, just show empty list
    } finally {
      setIsLoadingImages(false);
    }
  }

  async function handleConfirm() {
    try {
      setIsLoading(true);
      await request({
        method: "post",
        url: linksOf(workload).self! + "/-command",
        body: {
          type: "runCronWorkload",
          spec: {
            location: location,
            ...(activeDeadlineSeconds ? { activeDeadlineSeconds } : {}),
            containerOverrides: state.overrides
              .map((ov) => {
                if (!ov.isOverridden) {
                  return undefined;
                }
                let res: any = {
                  name: ov.name,
                };
                if (ov.command) {
                  res.command = ov.command;
                }
                if (ov.memory.value) {
                  res.memory = String(ov.memory.value) + String(ov.memory.suffix || "Mi");
                }
                if (ov.cpu.value) {
                  res.cpu = String(ov.cpu.value) + String(ov.cpu.suffix || "m");
                }
                const imageValue = ov.image.isExternal ? ov.image.externalValue : ov.image.cplnValue;
                if (imageValue) {
                  res.image = imageValue;
                }
                if (ov.args.length > 0) {
                  res.args = ov.args.map((a) => a.name);
                }
                if (ov.env.length > 0) {
                  res.env = ov.env.map((e) => ({ name: e.name, value: e.value }));
                }
                return res;
              })
              .filter(Boolean),
          },
        },
      });
      onDone();
      notification.success({
        message: "Success",
        description: "Created Command.",
      });
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed to start cron job",
        description: errorMessage,
      });
      setIsLoading(false);
    }
  }

  const selectedOverride = state.overrides.find((o) => o.name === state.selectedName)!;

  const isValid = !!location;

  return (
    <Modal
      width={600}
      open={visible}
      maskClosable={false}
      destroyOnHidden
      okText={`Start Cron Job`}
      cancelButtonProps={{ disabled: isLoading }}
      okButtonProps={{ disabled: isLoading || !isValid, loading: isLoading }}
      onCancel={onClose}
      onOk={handleConfirm}
    >
      <div className="flex flex-col gap-4">
        <div className="text-2xl">Start Cron Job Now</div>

        <FormItem label="Location">
          {locationFetchFailed && availableLocations.length === 0 ? (
            <Input
              value={location || undefined}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Enter a location"
            />
          ) : (
            <Select
              options={availableLocations.map((aL) => ({ label: aL, value: aL }))}
              value={location || undefined}
              onChange={(value) => setLocation(value)}
              placeholder="Select a location"
            />
          )}
        </FormItem>

        <FormItem label="Active Deadline (seconds)">
          <InputNumber
            min={1}
            precision={0}
            placeholder="Optional"
            value={activeDeadlineSeconds}
            onChange={(value) => setActiveDeadlineSeconds(value)}
            className="w-full"
            controls={false}
          />
        </FormItem>

        {state.containerNames.length > 0 ? (
          <>
            <Divider orientation="left" orientationMargin={0} style={{ marginBottom: 0 }}>
              Container Overrides
            </Divider>
            <Tabs
              size="small"
              onChange={(n) => dispatch({ type: RunCronReducerActionType.SELECT_CONTAINER, payload: n })}
              items={state.containerNames.map((n) => ({
                label: n,
                key: n,
                children: (
                  <div className="flex flex-col gap-4 pt-2">
                    <Checkbox
                      checked={selectedOverride.isOverridden}
                      onChange={() =>
                        dispatch({ type: RunCronReducerActionType.OVERRIDE, payload: !selectedOverride.isOverridden })
                      }
                    >
                      Override "{n}" Container Spec
                    </Checkbox>

                    {selectedOverride.isOverridden ? (
                      <>
                        <FormItem label="Command">
                          <Input.TextArea
                            rows={3}
                            placeholder="Enter command"
                            value={selectedOverride.command}
                            onChange={(e) =>
                              dispatch({ type: RunCronReducerActionType.COMMAND, payload: e.target.value })
                            }
                          />
                        </FormItem>

                        <div className="flex flex-col gap-2">
                          <div className="flex items-center justify-between">
                            <label className="text-sm color-label">Args</label>
                            <Button
                              onClick={() => dispatch({ type: RunCronReducerActionType.ADD_ARG, payload: "" })}
                              type="primary"
                              className="action"
                              size="small"
                            >
                              Add
                            </Button>
                          </div>
                          {selectedOverride.args.length === 0 ? (
                            <div className="text-sm color-label-secondary">No argument overrides</div>
                          ) : (
                            <div className="flex flex-col gap-2">
                              {selectedOverride.args.map((arg) => (
                                <div key={arg.id} className="flex items-center gap-2">
                                  <Input
                                    placeholder="Argument value"
                                    value={arg.name}
                                    onChange={(e) =>
                                      dispatch({
                                        type: RunCronReducerActionType.CHANGE_ARG,
                                        payload: { ...arg, name: e.target.value },
                                      })
                                    }
                                    className="flex-1"
                                  />
                                  <Button
                                    type="text"
                                    danger
                                    icon={<MinusCircleOutlined />}
                                    onClick={() =>
                                      dispatch({ type: RunCronReducerActionType.REMOVE_ARG, payload: arg.id })
                                    }
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="flex gap-2">
                          <FormItem label="CPU" className="flex-1">
                            <Input
                              placeholder="500"
                              value={selectedOverride.cpu.value}
                              onChange={(e) =>
                                dispatch({
                                  type: RunCronReducerActionType.SET_CPU,
                                  payload: { ...selectedOverride.cpu, value: e.target.value },
                                })
                              }
                            />
                          </FormItem>
                          <FormItem label="Unit" className="w-40">
                            <Select
                              value={selectedOverride.cpu.suffix || "m"}
                              options={cpuUnitOptions}
                              onChange={(suffix) =>
                                dispatch({
                                  type: RunCronReducerActionType.SET_CPU,
                                  payload: { ...selectedOverride.cpu, suffix },
                                })
                              }
                            />
                          </FormItem>
                        </div>

                        <div className="flex gap-2">
                          <FormItem label="Memory" className="flex-1">
                            <Input
                              placeholder="256"
                              value={selectedOverride.memory.value}
                              onChange={(e) =>
                                dispatch({
                                  type: RunCronReducerActionType.SET_MEMORY,
                                  payload: { ...selectedOverride.memory, value: e.target.value },
                                })
                              }
                            />
                          </FormItem>
                          <FormItem label="Unit" className="w-40">
                            <Select
                              value={selectedOverride.memory.suffix || "Mi"}
                              options={memoryUnitOptions}
                              onChange={(suffix) =>
                                dispatch({
                                  type: RunCronReducerActionType.SET_MEMORY,
                                  payload: { ...selectedOverride.memory, suffix },
                                })
                              }
                            />
                          </FormItem>
                        </div>

                        <div className="flex flex-col gap-2">
                          <label className="text-sm color-label">Image</label>
                          <RadioCardGroup
                            value={selectedOverride.image.isExternal}
                            onChange={(value) =>
                              dispatch({
                                type: RunCronReducerActionType.SET_IMAGE_IS_EXTERNAL,
                                payload: value,
                              })
                            }
                            options={[
                              {
                                label: "Control Plane",
                                value: false,
                                boldLabel: true,
                                children: (
                                  <div className="flex items-center gap-2 text-sm">
                                    <img
                                      className="object-contain"
                                      style={{ width: 16, height: 16 }}
                                      src={`/resources/logos/controlPlaneLogoOnly.svg`}
                                    />
                                    <span>Use an image from your CPLN repository</span>
                                  </div>
                                ),
                              },
                              {
                                label: "External",
                                value: true,
                                boldLabel: true,
                                children: (
                                  <div className="flex items-center gap-2 text-sm">
                                    <ImageIcon className="container-image-icon" style={{ width: 16, height: 16 }} />
                                    <span>Use an image from an external registry</span>
                                  </div>
                                ),
                              },
                            ]}
                          />
                          {selectedOverride.image.isExternal ? (
                            <Input
                              placeholder="nginx:latest"
                              value={selectedOverride.image.externalValue}
                              onChange={(e) =>
                                dispatch({
                                  type: RunCronReducerActionType.SET_IMAGE_EXTERNAL_VALUE,
                                  payload: e.target.value,
                                })
                              }
                            />
                          ) : (
                            <AutoComplete
                              placeholder="Select an image"
                              value={selectedOverride.image.cplnValue}
                              onChange={(value) =>
                                dispatch({
                                  type: RunCronReducerActionType.SET_IMAGE_CPLN_VALUE,
                                  payload: value,
                                })
                              }
                              options={images.map((img) => ({
                                label: img.name,
                                value: img.name,
                              }))}
                              filterOption={(inputValue, option) =>
                                option?.value?.toLowerCase().includes(inputValue.toLowerCase()) ?? false
                              }
                              disabled={isLoadingImages}
                            />
                          )}
                        </div>

                        <div className="flex flex-col gap-2">
                          <div className="flex items-center justify-between">
                            <label className="text-sm color-label">Environment Variables</label>
                            <Button
                              onClick={() => dispatch({ type: RunCronReducerActionType.ADD_ENV, payload: "" })}
                              type="primary"
                              className="action"
                              size="small"
                            >
                              Add
                            </Button>
                          </div>
                          {selectedOverride.env.length === 0 ? (
                            <div className="text-sm color-label-secondary">No environment variable overrides</div>
                          ) : (
                            <div className="flex flex-col gap-2">
                              {selectedOverride.env.map((env) => (
                                <div key={env.index} className="flex items-center gap-2">
                                  <Input
                                    placeholder="Name"
                                    value={env.name}
                                    onChange={(e) =>
                                      dispatch({
                                        type: RunCronReducerActionType.CHANGE_ENV,
                                        payload: { index: env.index, name: e.target.value, value: env.value },
                                      })
                                    }
                                    className="flex-1"
                                  />
                                  <Input
                                    placeholder="Value"
                                    value={env.value}
                                    onChange={(e) =>
                                      dispatch({
                                        type: RunCronReducerActionType.CHANGE_ENV,
                                        payload: { index: env.index, name: env.name, value: e.target.value },
                                      })
                                    }
                                    className="flex-1"
                                  />
                                  <Button
                                    type="text"
                                    danger
                                    icon={<MinusCircleOutlined />}
                                    onClick={() =>
                                      dispatch({ type: RunCronReducerActionType.REMOVE_ENV, payload: env.index })
                                    }
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </>
                    ) : null}
                  </div>
                ),
              }))}
            />
          </>
        ) : null}
      </div>
    </Modal>
  );
};
